import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import ScrollTop from "../features/ScrollTop";

export default function MainForBuyers() {
  const initialFormState = {
    name: "",
    phone: "",
    email: "",
    propertyType: "",
    budget: "",
    location: "",
    space: "",
    amenities: "",
    projectStage: "",
    dealBreakers: "",
    buyingIntent: "",
  };

  const [form, setForm] = useState(initialFormState);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    const artTimer = Math.floor(Math.random() * (5000 - 1000 + 1)) + 1000;

    if (!agreed) {
      setError("You must agree to the Privacy Policy and Terms & Conditions.");
      return;
    }

    setError("");
    console.log("Investor Requirements Submitted:", form);

    // Simulate success
    setTimeout(() => {
      setShowSuccess(true);

      // Reset form
      setForm(initialFormState);
      setAgreed(false);
      setLoading(false);
    }, artTimer);

    // Hide success message after 10 seconds
    setTimeout(() => {
      setShowSuccess(false);
    }, 15000);
  };

  useEffect(() => {
    document.title = "Investor Requirements | AttnQ";
  }, []);

  return (
    <main className="h-fit w-full px-6 lg:px-20 py-6 mt-14 relative shadow">
      <ScrollTop />
      <h2 className="text-2xl font-semibold text-center pb-6">
        Submit Your Requirements
      </h2>

      {/* ✅ Success Message */}
      {showSuccess && (
        <div className="absolute inset-0 m-auto w-max h-max bg-green-100 border border-green-400 text-green-800 px-4 py-5 rounded-lg shadow z-10">
          Thank you for submitting your requirement. You are now queued and our
          vetted brokers with any property match will contact you soon.
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="bg-white border rounded-xl p-8 lg:px-16 shadow-sm flex flex-col gap-6"
      >
        {/* ======== Input Fields ======== */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">Full Name</label>
            <input
              type="text"
              name="name"
              value={form.name || ""}
              onChange={handleChange}
              placeholder="Enter your full name"
              className="w-full border rounded-lg px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              name="phone"
              value={form.phone || ""}
              onChange={handleChange}
              placeholder="e.g., +91 9876543210"
              className="w-full border rounded-lg px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={form.email || ""}
              onChange={handleChange}
              placeholder="you@example.com"
              className="w-full border rounded-lg px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Property Type
            </label>
            <select
              name="propertyType"
              value={form.propertyType}
              onChange={handleChange}
              className="w-full border rounded-lg px-3 py-2"
              required
            >
              <option value="">Select type</option>
              <option value="Residential">Residential</option>
              <option value="Commercial">Commercial</option>
              <option value="Plot">Plot</option>
              <option value="Mixed-use">Mixed-use</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Budget</label>
            <input
              type="number"
              name="budget"
              placeholder="e.g., 20000000"
              value={form.budget}
              onChange={handleChange}
              className="w-full border rounded-lg px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Preferred Location(s)
            </label>
            <input
              type="text"
              name="location"
              placeholder="City, Area, Landmark"
              value={form.location}
              onChange={handleChange}
              className="w-full border rounded-lg px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Minimum Space Required (sq ft)
            </label>
            <input
              type="number"
              name="space"
              value={form.space}
              onChange={handleChange}
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">
              Desired Amenities
            </label>
            <input
              type="text"
              name="amenities"
              placeholder="e.g., parking, lift, security"
              value={form.amenities}
              onChange={handleChange}
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Project Stage
            </label>
            <select
              name="projectStage"
              value={form.projectStage}
              onChange={handleChange}
              className="w-full border rounded-lg px-3 py-2"
            >
              <option value="">Any</option>
              <option value="Under Construction">Under Construction</option>
              <option value="Ready to Move">Ready to Move</option>
              <option value="Pre-Launch">Pre-Launch</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Deal-breakers
            </label>
            <input
              type="text"
              name="dealBreakers"
              placeholder="e.g., no resale, no ground floor"
              value={form.dealBreakers}
              onChange={handleChange}
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">
              Buying Intent
            </label>
            <select
              name="buyingIntent"
              value={form.buyingIntent}
              onChange={handleChange}
              className="w-full border rounded-lg px-3 py-2"
            >
              <option value="">Select intent</option>
              <option value="Actively Searching">Actively Searching</option>
              <option value="Within 3 Months">Within 3 Months</option>
              <option value="Exploring Options">Exploring Options</option>
            </select>
          </div>
        </div>

        {/* ======== Agreement Checkbox ======== */}
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={agreed}
            onChange={() => setAgreed(!agreed)}
            className="mt-1 h-4 w-4 border rounded-sm text-[#1D4753] focus:ring-[#1D4753]"
          />
          <span className="text-sm">
            I agree to the{" "}
            <Link to="/terms-conditions">
              <span className="text-[#dc9209] hover:underline">
                Terms &amp; Conditions
              </span>{" "}
            </Link>
            and{" "}
            <Link to="/privacy-policy">
              <span className="text-[#dc9209] hover:underline">
                Privacy Policies.
              </span>
            </Link>
          </span>
        </label>

        {/* ======== Error Message ======== */}
        {error && <p className="text-red-600 text-sm">{error}</p>}

        {/* ======== Submit ======== */}
        <div>
          <button
            type="submit"
            disabled={!agreed}
            className={`bg-[#163944] text-white font-medium p-3 rounded-lg disabled:opacity-60 flex items-center justify-center
              ${
                agreed
                  ? "bg-[#1D4753] text-white hover:bg-[#163944] cursor-pointer"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
          >
            {loading ? (
              <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
            ) : (
              "Submit Requirements"
            )}
          </button>
        </div>
      </form>
    </main>
  );
}
